const SITE_URL = import.meta.env.VITE_SITE_URL || "https://pandabuy.courses";
const SITE_NAME = "PandaBuy.Courses";
const SITE_DESCRIPTION = "Discover the ultimate pandabuy spreadsheet hub with 1000+ curated finds across shoes, hoodies, jackets, accessories and more. Updated daily with viral TikTok and Reddit spreadsheet lists.";
const DEFAULT_IMAGE = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg";

interface WebsiteJsonLdProps {
  pageUrl?: string;
}

interface ArticleJsonLdProps {
  pageUrl: string;
  title: string;
  description: string;
  imageUrl?: string;
  datePublished?: string;
  dateModified?: string;
  authorName?: string;
}

export function WebsiteJsonLd({ pageUrl }: WebsiteJsonLdProps) {
  const url = pageUrl ? `${SITE_URL.replace(/\/+$/, "")}${pageUrl.startsWith("/") ? pageUrl : `/${pageUrl}`}` : SITE_URL;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    description: SITE_DESCRIPTION,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL.replace(/\/+$/, "")}/?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };

  const pageJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "PandaBuy Spreadsheet Hub 2026 — Curated Finds & Shopping Lists",
    url,
    description: SITE_DESCRIPTION,
    isPartOf: {
      "@type": "WebSite",
      name: SITE_NAME,
      url: SITE_URL,
    },
    about: {
      "@type": "Thing",
      name: "PandaBuy Spreadsheet",
      description: "Curated pandabuy spreadsheet finds, viral replica lists, and budget-to-luxury shopping collections",
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageJsonLd) }}
      />
    </>
  );
}

export function ArticleJsonLd({
  pageUrl,
  title,
  description,
  imageUrl,
  datePublished,
  dateModified,
  authorName,
}: ArticleJsonLdProps) {
  const url = `${SITE_URL.replace(/\/+$/, "")}${pageUrl.startsWith("/") ? pageUrl : `/${pageUrl}`}`;
  const pubDate = datePublished || "2026-06-01";
  const modDate = dateModified || "2026-06-29";

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    image: imageUrl || DEFAULT_IMAGE,
    datePublished: pubDate,
    dateModified: modDate,
    author: {
      "@type": "Organization",
      name: authorName || SITE_NAME,
      url: SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: {
        "@type": "ImageObject",
        url: DEFAULT_IMAGE,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: SITE_URL,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: title,
        item: url,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
    </>
  );
}